import type {TFunction} from "i18next";
import {
    ReservationSource,
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";

/**
 * Reservation trigger filters, contract mirrored by the API: `sources` absent = every source (legacy nodes);
 * `sources: []` never fires; `statuses` absent or empty = any status change.
 */

export const RESERVATION_SOURCE_ORDER: ReservationSource[] = [
    ReservationSource.BOOKING_ENGINE,
    ReservationSource.GUEST_APP,
    ReservationSource.DASHBOARD,
    ReservationSource.PMS,
    ReservationSource.SYSTEM
];

/** New triggers leave out PMS and SYSTEM: a PMS full sync imports in bulk and would fire once per row. */
export const DEFAULT_RESERVATION_SOURCES: string[] = [
    ReservationSource.BOOKING_ENGINE,
    ReservationSource.GUEST_APP,
    ReservationSource.DASHBOARD
];

export const RESERVATION_SOURCE_LABEL_KEYS: Record<ReservationSource, string> = {
    [ReservationSource.BOOKING_ENGINE]: 'Booking engine',
    [ReservationSource.GUEST_APP]: 'Guest app',
    [ReservationSource.DASHBOARD]: 'Dashboard',
    [ReservationSource.PMS]: 'PMS',
    [ReservationSource.SYSTEM]: 'System'
};

export const RESERVATION_STATUS_ORDER: ReservationStatus[] = [
    ReservationStatus.PENDING,
    ReservationStatus.OPTION,
    ReservationStatus.CONFIRMED,
    ReservationStatus.ACTIVE,
    ReservationStatus.COMPLETED,
    ReservationStatus.UNCOMPLETED,
    ReservationStatus.CANCELLED,
    ReservationStatus.NO_SHOW
];

/** The same keys ReservationStatusElement renders. */
export const reservationStatusLabel = (status: string, t: TFunction): string =>
    t(`Reservation status ${status}`);

export const reservationSourceLabel = (source: string, t: TFunction): string => {
    const key = RESERVATION_SOURCE_LABEL_KEYS[source as ReservationSource];
    return key ? t(key) : source;
};

const asList = (value: unknown): string[] => (Array.isArray(value) ? value.filter(entry => !!entry) : []);

export const readSources = (config?: Record<string, any> | null): string[] | undefined => (
    Array.isArray(config?.sources) ? config!.sources.filter((entry: unknown) => !!entry) : undefined
);

export const readStatuses = (config?: Record<string, any> | null): string[] => asList(config?.statuses);

/** Absent or all sources narrow nothing. */
export const hasSourceFilter = (sources: string[] | undefined): boolean =>
    !!sources && sources.length > 0 && sources.length < RESERVATION_SOURCE_ORDER.length;

/** A node whose sources list is present but empty matches no reservation at all. */
export const isSourceFilterEmpty = (sources: string[] | undefined): boolean =>
    !!sources && sources.length === 0;

const inOrder = <T extends string>(order: T[], values: string[]): string[] => {
    const known = order.filter(entry => values.includes(entry));
    const unknown = values.filter(value => !order.includes(value as T));
    return [...known, ...unknown];
};

export const formatSourceList = (sources: string[], t: TFunction): string =>
    inOrder(RESERVATION_SOURCE_ORDER, sources).map(source => reservationSourceLabel(source, t)).join(', ');

export const formatStatusList = (statuses: string[], t: TFunction): string =>
    inOrder(RESERVATION_STATUS_ORDER, statuses).map(status => reservationStatusLabel(status, t)).join(', ');

/**
 * Id filters: a plural id list plus index-aligned name snapshots; older nodes carry the singular pair.
 * A non-empty plural list wins, else a non-blank singular id (as the API's TriggerConfigFilters), else no
 * restriction — unlike `sources`, where empty never fires.
 */

export interface IdFilterKeys {
    ids: string;
    /** Index-aligned with `ids`. */
    names: string;
    /** Legacy singular pair. */
    id: string;
    name: string;
}

export interface StoredIdFilter {
    ids: string[];
    /** The only name left for an id the catalogue dropped. */
    names: Record<string, string>;
}

export interface IdFilterSelection {
    id: string;
    name?: string;
}

export const SECTOR_FILTER_KEYS: IdFilterKeys = {
    ids: 'sectorIds', names: 'sectorNames', id: 'sectorId', name: 'sectorName'
};

export const SERVICE_FILTER_KEYS: IdFilterKeys = {
    ids: 'serviceIds', names: 'serviceNames', id: 'serviceId', name: 'serviceName'
};

const isFilledString = (value: unknown): value is string => typeof value === 'string' && !!value.trim();

export const readIdFilter = (
    config: Record<string, any> | null | undefined,
    keys: IdFilterKeys
): StoredIdFilter => {
    const storedIds = config?.[keys.ids];
    const ids = Array.isArray(storedIds) ? storedIds.filter(isFilledString) : [];

    if (ids.length) {
        const storedNames = Array.isArray(config?.[keys.names]) ? config![keys.names] : [];
        const names: Record<string, string> = {};

        // Pair names by index before blank ids are dropped.
        storedIds.forEach((id: unknown, index: number) => {
            const name = storedNames[index];
            if (isFilledString(id) && isFilledString(name)) names[id] = name;
        });

        return {ids, names};
    }

    const legacyId = config?.[keys.id];
    if (isFilledString(legacyId)) {
        const legacyName = config?.[keys.name];
        return {ids: [legacyId], names: isFilledString(legacyName) ? {[legacyId]: legacyName} : {}};
    }

    return {ids: [], names: {}};
};

/** Writes the plural pair and clears the singular pair, so the API never sees two filters. */
export const writeIdFilter = (
    selection: IdFilterSelection[],
    keys: IdFilterKeys
): Record<string, any> => ({
    [keys.ids]: selection.map(entry => entry.id),
    [keys.names]: selection.map(entry => entry.name ?? ''),
    [keys.id]: undefined,
    [keys.name]: undefined
});

/** A nameless id still shows ("Unavailable entry") — a filter that narrows must never read as "any". */
export const formatIdFilterNames = (filter: StoredIdFilter, t: TFunction): string =>
    filter.ids.map(id => filter.names[id] || t('Unavailable entry')).join(', ');

export const readSectorFilter = (config?: Record<string, any> | null): StoredIdFilter =>
    readIdFilter(config, SECTOR_FILTER_KEYS);

export const readServiceFilter = (config?: Record<string, any> | null): StoredIdFilter =>
    readIdFilter(config, SERVICE_FILTER_KEYS);

export const writeSectorFilter = (selection: IdFilterSelection[]): Record<string, any> =>
    writeIdFilter(selection, SECTOR_FILTER_KEYS);

export const writeServiceFilter = (selection: IdFilterSelection[]): Record<string, any> =>
    writeIdFilter(selection, SERVICE_FILTER_KEYS);
